import React, { useEffect, useState } from 'react'
import { selectThemeStyle } from '../../../features/setting/setting.slice.ts'
import { useAppSelector } from '../../../redux/hooks.ts'
import { useDispatch } from 'react-redux'
import { convertPxToNumber, isV2Ready } from '../../../utils/helpers.ts'
import IdeyaTypography from '../../../common/antd/ideya-typography.tsx'
import {
  useListFirmOptionsQuery,
  useListUserOptionsQuery,
} from '../../../apis/option/option-api.api.ts'
import {
  selectIdeaListInput,
  setBookId,
  setIdeaListCategory,
  setIdeaListContributor,
  setIdeaListDateRange,
  setIdeaListFirm,
  toggleOnlyMyIdea,
  toggleOnlyPremiumLeague,
  toggleShowLiquidatedIdeas,
} from '../../../redux/slices/idea-list-input.slice.ts'
import { IdeyaButton } from '../../../common/antd/ideya-button.tsx'
import { selectUser } from '../../../features/user/user.slice.ts'
import { UserInfo } from '../../../apis/auth/auth.def.ts'
import {
  canCreateIdeaPermissions,
  canSeeCategorySelectPermissions,
  canSeeContributorSelectPermissions,
  canSeeFirmSelectPermissions,
  canSeeViewStatistics,
  hasPermission,
} from '../../../utils/permissions.ts'
import { useIsMobile } from '../../../common/hooks/use-is-mobile.ts'
import Spin from 'antd/es/spin/index'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import Select from 'antd/es/select/index'
import Switch from 'antd/es/switch/index'
import { useLocation } from 'react-router-dom'
import {
  getCategoryOptions,
  getMatchedCombinedCategory,
} from '../../../utils/selector-options.ts'
import { useTranslation } from 'react-i18next'
import Drawer from 'antd/es/drawer'
import { Dashboard } from '../../summary/dashboard.tsx'
import { BarChartOutlined } from '@ant-design/icons'
import { useLazyGetBookDetailsQuery } from '../../../apis/book/book-api.api.ts'
import IdeyaRangePicker from '../../../common/antd/ideya-rangepicker.tsx'
import dayjs from 'dayjs'

interface IdeaListUserControlsProps {
  onCreateIdea: () => void
}

export const IdeaListUserControls: React.FC<IdeaListUserControlsProps> = ({
  onCreateIdea,
}) => {
  const themeStyle = useAppSelector(selectThemeStyle)
  const ideaListInput = useAppSelector(selectIdeaListInput)
  const dispatch = useDispatch()
  const userInfo: UserInfo = useAppSelector(selectUser)
  const isMobile = useIsMobile(768)
  const location = useLocation()
  const [region, netCategory] = userInfo.groupName
    ? userInfo.groupName.split('-').map((s) => s.trim())
    : ideaListInput.category
      ? ideaListInput.category.split('-').map((s) => s.trim())
      : [undefined, undefined]
  const { t } = useTranslation()
  const [openDashboard, setOpenDashboard] = useState<boolean>(false)
  const [contributorName, setContributorName] = useState<string>('')

  const canCreateIdea = hasPermission(
    canCreateIdeaPermissions,
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )

  const canViewStatistics =
    hasPermission(
      canSeeViewStatistics,
      userInfo.firmRole,
      userInfo.groupRole,
      userInfo.role
    ) && contributorName

  const canFilterByFirm = hasPermission(
    canSeeFirmSelectPermissions,
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )
  const canFilterByCategory = hasPermission(
    canSeeCategorySelectPermissions,
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )

  const canFilterByContributor = hasPermission(
    canSeeContributorSelectPermissions,
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )

  const { data: firmOptionData, isLoading: isLoadingFirmOptions } =
    useListFirmOptionsQuery()

  const { data: userOptionData, isLoading: isLoadingUserOptions } =
    useListUserOptionsQuery({
      firmId: userInfo.firmId ?? ideaListInput.firm,
      netCategory,
      region,
    })

  const [
    triggerGetBookDetails,
    {
      data: bookDetailsData,
      isFetching: isFetchingBookDetails,
      isLoading: isLoadingBookDetails,
    },
  ] = useLazyGetBookDetailsQuery()

  const [contributorValue, setContributorValue] = React.useState<string>(
    ideaListInput.contributor
  )

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const userId = params.get('userId')
    const startDate = params.get('startDate')
    const endDate = params.get('endDate')
    if (userId) {
      dispatch(setIdeaListContributor(userId))
      setContributorValue(userId)
      const user = userOptionData?.data.find((u) => u.value === userId)
      if (user) {
        dispatch(setBookId(user.bookId))
        setContributorName(user.label)
        triggerGetBookDetails({ id: user.bookId }) // fetch details here
      }
    }

    if (startDate && endDate) {
      dispatch(setIdeaListDateRange([startDate, endDate]))
    }
  }, [location.search, dispatch, userOptionData?.data])

  useEffect(() => {
    if (
      contributorValue &&
      userOptionData &&
      !userOptionData.data.some((user) => user.value === contributorValue)
    ) {
      dispatch(setIdeaListContributor(''))
      setContributorName('')
      setContributorValue('')
    } else {
      const user = userOptionData?.data.find(
        (u) => u.value === contributorValue
      )
      if (user) {
        dispatch(setBookId(user.bookId))
        setContributorName(user.label)
      }
      if (bookDetailsData) {
        dispatch(setIdeaListFirm(bookDetailsData.data.firmId))
        const matchedCategory = getMatchedCombinedCategory(
          bookDetailsData.data.region,
          bookDetailsData.data.netCategory,
          isMobile
        )
        if (matchedCategory) {
          dispatch(setIdeaListCategory(matchedCategory))
        } else {
          dispatch(setIdeaListCategory(bookDetailsData.data.netCategory))
        }
      }
    }
  }, [userOptionData, contributorValue, dispatch, bookDetailsData])

  if (
    isLoadingFirmOptions ||
    isLoadingUserOptions ||
    isFetchingBookDetails ||
    isLoadingBookDetails
  ) {
    return <Spin />
  }

  return (
    <>
      <Row
        align={'bottom'}
        justify={'space-between'}
        style={{
          marginTop: themeStyle.marginBase,
          marginBottom: themeStyle.marginSM,
        }}
      >
        <Col md={20}>
          <Row
            gutter={[
              convertPxToNumber(themeStyle.marginBase),
              convertPxToNumber(themeStyle.marginBase),
            ]}
          >
            {canFilterByCategory && (
              <>
                <Col xs={24} md={6}>
                  {!isMobile && (
                    <Row style={{ paddingBottom: themeStyle.paddingXS }}>
                      <IdeyaTypography.Text>
                        {t('common.user_controls.category')}
                      </IdeyaTypography.Text>
                    </Row>
                  )}
                  <Row>
                    <Select
                      showSearch
                      value={ideaListInput.category}
                      style={{ width: '100%' }}
                      onChange={(value) => dispatch(setIdeaListCategory(value))}
                      options={getCategoryOptions(isMobile)}
                    />
                  </Row>
                </Col>
              </>
            )}
            {canFilterByFirm && (
              <Col xs={24} md={6}>
                {!isMobile && (
                  <Row style={{ paddingBottom: themeStyle.paddingXS }}>
                    <IdeyaTypography.Text>
                      {t('common.user_controls.firm')}
                    </IdeyaTypography.Text>
                  </Row>
                )}
                <Row>
                  <Select
                    showSearch
                    value={ideaListInput.firm}
                    style={{ width: '100%' }}
                    onChange={(value) => {
                      dispatch(setIdeaListFirm(value))
                      // If firm changes, reset contributor back to all
                      dispatch(setIdeaListContributor(''))
                    }}
                    options={[
                      {
                        value: '',
                        label: isMobile
                          ? t('common.user_controls.all_firms')
                          : t('common.user_controls.all'),
                      },
                      ...(firmOptionData?.data.map((firm) => ({
                        value: firm.value,
                        label: firm.label,
                      })) ?? []),
                    ]}
                    filterOption={(input, option) =>
                      (option?.label as string)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Row>
              </Col>
            )}
            {canFilterByContributor && (
              <Col xs={24} md={6}>
                {!isMobile && (
                  <Row style={{ paddingBottom: themeStyle.paddingXS }}>
                    <IdeyaTypography.Text>
                      {t('common.user_controls.contributor')}
                    </IdeyaTypography.Text>
                  </Row>
                )}
                <Row>
                  <Select
                    showSearch
                    value={contributorValue}
                    style={{ width: '100%' }}
                    onChange={(value) => {
                      const selectedContributor = userOptionData?.data.find(
                        (user) => user.value === value
                      )
                      setContributorName(selectedContributor?.label ?? '')
                      dispatch(setIdeaListContributor(value))
                      setContributorValue(value)
                    }}
                    options={[
                      {
                        value: '',
                        label: isMobile
                          ? t('common.user_controls.all_contributors')
                          : t('common.user_controls.all'),
                      },
                      ...(userOptionData?.data.map((user) => ({
                        value: user.value,
                        label: user.label,
                      })) ?? []),
                    ]}
                    filterOption={(input, option) =>
                      (option?.label as string)
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Row>
              </Col>
            )}
            <Col xs={24} md={6}>
              {!isMobile && (
                <Row style={{ paddingBottom: themeStyle.paddingXS }}>
                  <IdeyaTypography.Text>
                    {t('common.user_controls.time_interval')}
                  </IdeyaTypography.Text>
                </Row>
              )}
              <Row>
                <IdeyaRangePicker
                  allowClear={true}
                  value={[
                    ideaListInput.dateRange[0]
                      ? dayjs(ideaListInput.dateRange[0])
                      : null,
                    ideaListInput.dateRange[1]
                      ? dayjs(ideaListInput.dateRange[1])
                      : null,
                  ]}
                  onChange={(dates) => {
                    if (!dates || !dates[0] || !dates[1]) {
                      dispatch(setIdeaListDateRange([null, null]))
                    } else {
                      dispatch(
                        setIdeaListDateRange([
                          dates[0].toISOString(),
                          dates[1].toISOString(),
                        ])
                      )
                    }
                  }}
                  style={{ width: '100%' }}
                />
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row
        align={'middle'}
        justify={'space-between'}
        style={{ marginBottom: themeStyle.marginSM }}
      >
        <Col span={12}>
          <Row
            gutter={[
              convertPxToNumber(themeStyle.marginBase),
              convertPxToNumber(themeStyle.marginBase),
            ]}
          >
            <Col>
              <Row
                gutter={[
                  convertPxToNumber(themeStyle.marginXS),
                  convertPxToNumber(themeStyle.marginXS),
                ]}
              >
                <Col>
                  <Switch
                    value={ideaListInput.onlyMyIdea}
                    size={'small'}
                    onChange={(value) => dispatch(toggleOnlyMyIdea(value))}
                  />
                </Col>
                <Col>
                  <IdeyaTypography.Text>
                    {t('idea_list.user_controls.only_my_ideas')}
                  </IdeyaTypography.Text>
                </Col>
              </Row>
            </Col>
            <Col>
              <Row
                gutter={[
                  convertPxToNumber(themeStyle.marginXS),
                  convertPxToNumber(themeStyle.marginXS),
                ]}
              >
                <Col>
                  <Switch
                    value={ideaListInput.showLiquidatedIdeas}
                    size={'small'}
                    onChange={(value) =>
                      dispatch(toggleShowLiquidatedIdeas(value))
                    }
                  />
                </Col>
                <Col>
                  <IdeyaTypography.Text>
                    {isMobile
                      ? t('idea_list.user_controls.show_liquidated')
                      : t('idea_list.user_controls.show_liquidated_ideas')}
                  </IdeyaTypography.Text>
                </Col>
              </Row>
            </Col>
            {isV2Ready && (
              <Col>
                <Row
                  gutter={[
                    convertPxToNumber(themeStyle.marginXS),
                    convertPxToNumber(themeStyle.marginXS),
                  ]}
                >
                  <Col>
                    <Switch
                      value={ideaListInput.onlyPremiumLeague}
                      size={'small'}
                      onChange={(value) =>
                        dispatch(toggleOnlyPremiumLeague(value))
                      }
                    />
                  </Col>
                  <Col>
                    <IdeyaTypography.Text>
                      {t('idea_list.user_controls.only_premium_league')}
                    </IdeyaTypography.Text>
                  </Col>
                </Row>
              </Col>
            )}
          </Row>
        </Col>
        <Col span={12}>
          <Row
            gutter={[
              convertPxToNumber(themeStyle.marginXS),
              convertPxToNumber(themeStyle.marginXS),
            ]}
            justify={'end'}
          >
            {canViewStatistics && (
              <Col
                style={
                  isMobile
                    ? {
                        marginTop: 12,
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }
                    : { display: 'flex', justifyContent: 'flex-end' }
                }
              >
                <IdeyaButton
                  icon={<BarChartOutlined />}
                  onClick={() => setOpenDashboard(true)}
                >
                  {t('idea_list.view_statistics')}
                </IdeyaButton>
              </Col>
            )}
            {canCreateIdea && (
              <Col
                style={
                  isMobile
                    ? {
                        marginTop: 12,
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }
                    : { display: 'flex', justifyContent: 'flex-end' }
                }
              >
                <IdeyaButton onClick={onCreateIdea}>
                  {t('common.create_idea')}
                </IdeyaButton>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
      <Drawer
        open={openDashboard}
        onClose={() => setOpenDashboard(false)}
        width={'100%'}
        placement={'left'}
        title={`Dashboard for ${contributorName}`}
        destroyOnClose
        style={{
          background: `var(--background-image)`,
        }}
      >
        <Dashboard bookId={ideaListInput.bookId} />
      </Drawer>
    </>
  )
}
